import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowLeft, Search, Shield, AlertTriangle, Activity,
    MapPin, Clock, Filter, AlertOctagon, Info
} from 'lucide-react';
import { Alert, HazardType } from '../types';

interface IntelFeedPageProps {
    hazards: Alert[];
    onBack: () => void;
    onSelectHazard: (hazard: Alert) => void;
}

const IntelFeedPage: React.FC<IntelFeedPageProps> = ({ hazards, onBack, onSelectHazard }) => {
    const [filter, setFilter] = useState('');
    const [activeType, setActiveType] = useState<HazardType | 'ALL'>('ALL');
    const [minSeverity, setMinSeverity] = useState<'all' | 'critical' | 'high'>('all');

    const filteredHazards = useMemo(() => {
        if (!hazards || !Array.isArray(hazards)) return [];
        return hazards.filter(h => {
            const matchesSearch =
                h.type.toLowerCase().includes(filter.toLowerCase()) ||
                h.location.toLowerCase().includes(filter.toLowerCase());

            const matchesType = activeType === 'ALL' || h.type === activeType;

            let matchesSeverity = true;
            if (minSeverity === 'critical') matchesSeverity = h.severity === 'critical';
            if (minSeverity === 'high') matchesSeverity = (h.severity === 'critical' || h.severity === 'high');

            return matchesSearch && matchesType && matchesSeverity;
        });
    }, [hazards, filter, activeType, minSeverity]);

    return (
        <div className="h-[100dvh] flex flex-col bg-[#020817] text-white font-sans overflow-hidden">

            {/* HEADER AREA (Fixed) */}
            <div className="shrink-0 bg-[#020817]/80 backdrop-blur-xl border-b border-white/10 z-20">
                <div className="px-4 py-4">
                    <div className="flex items-center gap-3 mb-4">
                        <button
                            onClick={onBack}
                            className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition active:scale-95"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-xl font-black tracking-wide">INTEL FEED</h1>
                            <p className="text-[10px] text-gray-400 font-mono tracking-widest uppercase">
                                GLOBAL HAZARD DATABASE • {hazards.length} ACTIVE
                            </p>
                        </div>
                    </div>

                    {/* SEARCH & FILTER */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 text-gray-500" size={16} />
                            <input
                                type="text"
                                placeholder="Search region or threat..."
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[var(--neon-cyan)]/50 transition"
                            />
                        </div>
                        <button
                            onClick={() => setMinSeverity(minSeverity === 'all' ? 'high' : minSeverity === 'high' ? 'critical' : 'all')}
                            className={`px-3 rounded-lg border flex items-center gap-2 transition ${minSeverity === 'critical' ? 'bg-red-500/20 border-red-500 text-red-500' :
                                minSeverity === 'high' ? 'bg-orange-500/20 border-orange-500 text-orange-500' :
                                    'bg-white/5 border-white/10 text-gray-400'
                                }`}
                        >
                            <Filter size={16} />
                            <span className="text-xs font-bold uppercase">{minSeverity === 'all' ? 'ALL' : minSeverity}</span>
                        </button>
                    </div>
                </div>

                {/* TABS */}
                <div className="overflow-x-auto px-4 pb-3 flex gap-2 no-scrollbar">
                    {['ALL', HazardType.EARTHQUAKE, HazardType.FLOOD, HazardType.FIRE, HazardType.TRAFFIC].map((type) => (
                        <button
                            key={type}
                            onClick={() => setActiveType(type as any)}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition ${activeType === type
                                ? 'bg-[var(--neon-cyan)]/20 border-[var(--neon-cyan)] text-[var(--neon-cyan)]'
                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                                }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            {/* SCROLLABLE FEED LIST */}
            <div className="flex-1 overflow-y-auto overscroll-contain bg-gradient-to-b from-[#020817] to-[#0f172a] p-4 space-y-3 pb-safe-bottom">
                {filteredHazards.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-600">
                        <Search size={48} className="mb-4 opacity-50" />
                        <p className="text-sm">NO DATA FOUND</p>
                    </div>
                ) : (
                    filteredHazards.map((hazard) => {
                        const isCritical = hazard.severity === 'critical';
                        const isHistoric = hazard.description?.includes('Historic') || hazard.description?.includes('ARCHIVE');

                        return (
                            <motion.div
                                key={hazard.id}
                                layoutId={hazard.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={() => onSelectHazard(hazard)}
                                className={`
                  relative overflow-hidden rounded-2xl border backdrop-blur-md active:scale-[0.98] transition cursor-pointer
                  ${isCritical ? 'bg-red-950/20 border-red-500/30' : 'bg-[#1E293B]/40 border-white/5 hover:border-white/20'}
                `}
                            >
                                {/* Glow Effect for Critical */}
                                {isCritical && <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 blur-3xl -z-10" />}

                                <div className="flex p-4 gap-4">
                                    {/* Icon Column */}
                                    <div className="flex flex-col items-center gap-2">
                                        <div className={`
                        w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 border shadow-lg
                        ${isCritical ? 'bg-red-500/20 border-red-500/50 text-red-500' : 'bg-[#020817] border-white/10 text-gray-400'}
                    `}>
                                            {hazard.type === HazardType.EARTHQUAKE ? '⚡' :
                                                hazard.type === HazardType.FIRE ? '🔥' :
                                                    hazard.type === HazardType.FLOOD ? '🌊' :
                                                        hazard.type === HazardType.TRAFFIC ? '🚗' : '⚠️'}
                                        </div>
                                        {hazard.truthScore && (
                                            <div className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-bold text-emerald-400">
                                                {hazard.truthScore}%
                                            </div>
                                        )}
                                    </div>

                                    {/* Content Column */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <h3 className="font-bold text-white text-base leading-tight truncate pr-2">
                                                    {hazard.location}
                                                </h3>
                                            </div>
                                            <span className={`shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded border ${isCritical ? 'bg-red-500 text-black border-red-500' : 'bg-white/5 text-gray-400 border-white/10'
                                                }`}>
                                                {hazard.severity}
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400 mb-2 font-mono items-center">
                                            <span className="flex items-center gap-1">
                                                <Clock size={10} /> {hazard.time}
                                            </span>
                                            <span className="w-0.5 h-3 bg-white/10" />
                                            {hazard.magnitude && (
                                                <span className="flex items-center gap-1 text-[var(--neon-cyan)]">
                                                    <Activity size={10} /> Mag {hazard.magnitude.toFixed(1)}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1 text-gray-500">
                                                <MapPin size={10} /> {hazard.distance}
                                            </span>
                                        </div>

                                        <p className="text-xs text-gray-300 line-clamp-2 leading-relaxed opacity-80">
                                            {hazard.description}
                                        </p>

                                        {/* Footer Info */}
                                        <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-wider">
                                                <Shield size={10} /> {hazard.source}
                                            </div>
                                            <div className="text-[10px] font-bold text-[var(--neon-cyan)] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                                                VIEW <ArrowLeft className="rotate-180" size={10} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>

        </div>
    );
};

export default IntelFeedPage;
